import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNotifications } from "@/modules/Notifications/hooks/useNotifications";
import { VscClose } from "react-icons/vsc";
import { NotificationPopover } from "@/components/ui/NotificationPopover";
import EdocertLogo from "@assets/edocert-mobile-logo.svg";

const NotificationItem = ({
  type,
  title,
  description,
  time,
  hasAction,
  actionLabel,
  onAction,
  isRead,
}: {
  type: "error" | "success" | "info" | "warning";
  title: string;
  description: string;
  time: string;
  hasAction?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  isRead: boolean;
}) => (
  <div
    className={`p-4 border-b border-gray-200 ${
      !isRead ? "bg-edPrimaryColor-50" : ""
    }`}
  >
    <div className="flex gap-3 items-center">
      <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0 box-border p-1" >
        <img src={EdocertLogo} alt="logo" className="w-full h-full object-cover" />
      </div>

      <div className="flex-1">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
              {!isRead && (
                <span className="inline-flex items-center justify-center w-2 h-2 bg-edPrimaryColor-500 rounded-full" />
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
            {hasAction && actionLabel && (
              <button
                onClick={onAction}
                className="mt-2 text-sm font-medium text-edPrimaryColor-500 hover:text-red-800 uppercase"
              >
                {actionLabel}
              </button>
            )}
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {time}
          </span>
        </div>
      </div>
    </div>
  </div>
);

export function NotificationsPanel() {
  const { notifications, messages } = useNotifications();
  const [activeTab, setActiveTab] = useState("notifiche");

  const mapPriorityToType = (
    priority: string
  ): "error" | "success" | "info" | "warning" => {
    switch (priority) {
      case "URGENT":
        return "error";
      case "HIGH":
        return "warning";
      case "MEDIUM":
        return "info";
      case "LOW":
        return "info";
      default:
        return "info";
    }
  };

  const getRelativeTime = (dateString: string): string => {
    const diff = new Date().getTime() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "ora";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}g`;
  };

  return (
    <div className="w-[380px] bg-white rounded-lg shadow-xl relative">
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab("notifiche")}
          style={{
            outline: "0px solid transparent",
            border:"none"
          }}
          className={`flex-1 py-3 text-sm font-medium relative  ${
            activeTab === "notifiche"
              ? "text-edPrimaryColor-500"
              : "text-gray-500"
          }`}
        >
          Notifiche ({notifications.length})
          {activeTab === "notifiche" && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-edPrimaryColor-500"
              layoutId="activeTab"
            />
          )}
        </button>
        <button
           style={{
            outline: "0px solid transparent",
            border:"none"
          }}
          onClick={() => setActiveTab("messaggi")}
          className={`flex-1 py-3 text-sm font-medium relative ${
            activeTab === "messaggi"
              ? "text-edPrimaryColor-500"
              : "text-gray-500"
          }`}
        >
          Messaggi ({messages.length})
          {activeTab === "messaggi" && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-edPrimaryColor-500"
              layoutId="activeTab"
            />
          )}
        </button>
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: activeTab === "notifiche" ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: activeTab === "notifiche" ? 20 : -20 }}
            transition={{ duration: 0.15 }}
          >
            {activeTab === "notifiche" ? (
              notifications.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      type={mapPriorityToType(notification.priority)}
                      title={notification.title}
                      description={notification.message}
                      time={getRelativeTime(notification.createdAt)}
                      isRead={notification.readAt !== null}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  Nessuna notifica
                </div>
              )
            ) : messages.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {messages.map((message) => (
                  <NotificationItem
                    key={message.id}
                    type={mapPriorityToType(message.priority)}
                    title={message.title}
                    description={message.message}
                    time={getRelativeTime(message.createdAt)}
                    isRead={message.readAt !== null}
                  />
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                Nessun messaggio
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      {/* <NotificationPopover.Close >
        <button className="absolute top-2 right-2"    style={{
            outline: "0px solid transparent",
            border:"none"
          }}>
          <VscClose size={24} />
        </button>
      </NotificationPopover.Close> */}
    </div>
  );
}
